// ─────────────────────────────────────────────────────────────
// SentryErrorBoundary.tsx
//
// App-level error boundary. Wraps <App/> in src/main.tsx so any uncaught
// React render error is reported to Sentry (Sentry.ErrorBoundary calls
// captureException internally) and the user sees a themed full-page
// fallback instead of a blank screen.
// ─────────────────────────────────────────────────────────────

import { ReactNode } from "react";
import * as Sentry from "@sentry/react";
import Button from "../../components/Button/Button";

const Fallback = () => (
  <div className="flex h-screen w-full flex-col items-center justify-center gap-4 bg-pure-white dark:bg-pure-white-dk px-6 text-center">
    <h1 className="text-xl font-semibold text-heading-text dark:text-heading-text-dk">
      Something went wrong
    </h1>
    <p className="max-w-md text-base text-ghost-text dark:text-ghost-text-dk">
      An unexpected error occurred and has been reported to our team. Please
      reload the page to continue.
    </p>
    <Button
      innerText="Reload"
      onClick={() => window.location.reload()}
      buttonContainerClass="bg-verba-blue dark:bg-verba-blue-dk text-pure-white rounded-lg px-6 py-2"
    />
  </div>
);

const SentryErrorBoundary = ({ children }: { children: ReactNode }) => (
  <Sentry.ErrorBoundary fallback={<Fallback />}>{children}</Sentry.ErrorBoundary>
);

export default SentryErrorBoundary;
